import React, { useCallback, useEffect, useState } from 'react';
import { Person } from '../../types';
import { adminProfileApi } from '../../services/profileMutationsApi';
import { directoryValidationApi } from '../../services/api';
import { requestDirectoryReadonlyToken } from '../../services/googleDirectoryToken';
import {
  isWorkspaceContactPlaceholder,
  suggestCreativedockEmail,
} from '../../utils/creativedockEmailGuess';
import { looksLikeEmail } from '../../utils/looksLikeEmail';
import { isAllowedWorkspaceEmail } from '../../utils/allowedWorkspaceDomains';
import { useAuth } from '../../hooks/useAuth';

export function resolveHeaderEmail(
  contact: string | undefined,
  fullName: string,
  domain: string,
): { value: string; isSuggested: boolean } {
  const c = (contact || '').trim();
  if (looksLikeEmail(c) && !isWorkspaceContactPlaceholder(c)) {
    return { value: c, isSuggested: false };
  }
  const guessed = suggestCreativedockEmail(fullName, domain);
  if (guessed) return { value: guessed, isSuggested: true };
  return { value: c, isSuggested: false };
}

export interface ProfileHeaderEmailFieldProps {
  person: Person;
  workspaceDomain: string;
  canEdit: boolean;
  onContactSaved?: (updated: Person) => void;
}

/**
 * Work email shown beside directory "Validate" in profile header / magic-link UI.
 * HR/admin can edit inline; others see read-only text when an address is known.
 */
export const ProfileHeaderEmailField: React.FC<ProfileHeaderEmailFieldProps> = ({
  person,
  workspaceDomain,
  canEdit,
  onContactSaved,
}) => {
  const { isAuthenticated } = useAuth();
  const resolved = resolveHeaderEmail(person.Contact ?? undefined, person.Name || '', workspaceDomain);
  const [emailDraft, setEmailDraft] = useState(resolved.value);
  const [isSuggested, setIsSuggested] = useState(resolved.isSuggested);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [directoryValidated, setDirectoryValidated] = useState(false);
  const [directoryBusy, setDirectoryBusy] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);

  useEffect(() => {
    const next = resolveHeaderEmail(person.Contact ?? undefined, person.Name || '', workspaceDomain);
    setEmailDraft(next.value);
    setIsSuggested(next.isSuggested);
    setDirectoryValidated(false);
    setDirectoryError(null);
    setContactError(null);
  }, [person.id, person.Contact, person.Name, workspaceDomain]);

  const validationEmail = emailDraft.trim().toLowerCase();
  const isCompanyEmail =
    looksLikeEmail(validationEmail) &&
    !isWorkspaceContactPlaceholder(validationEmail) &&
    isAllowedWorkspaceEmail(validationEmail, workspaceDomain);
  const canValidate =
    import.meta.env.VITE_GOOGLE_CLIENT_ID && isAuthenticated && isCompanyEmail;

  const persistContact = useCallback(async () => {
    if (!canEdit || person.id == null || contactSaving) return;
    const next = emailDraft.trim();
    const stored = (person.Contact || '').trim();
    if (next === stored) return;
    if (next !== '' && !looksLikeEmail(next)) {
      setContactError('Enter a valid email address');
      return;
    }
    setContactSaving(true);
    setContactError(null);
    try {
      const updated = await adminProfileApi.patchOverviewFields(person.id, { contact: next });
      onContactSaved?.(updated);
      setIsSuggested(false);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : e instanceof Error
            ? e.message
            : 'Failed to save email';
      setContactError(typeof msg === 'string' ? msg : 'Failed to save email');
    } finally {
      setContactSaving(false);
    }
  }, [canEdit, person.id, person.Contact, emailDraft, contactSaving, onContactSaved]);

  const handleValidate = async () => {
    if (!canValidate) return;
    setDirectoryError(null);
    setDirectoryBusy(true);
    try {
      const accessToken = await requestDirectoryReadonlyToken();
      const { results } = await directoryValidationApi.validateEmails(accessToken, [validationEmail]);
      const row = results[0];
      if (!row) {
        setDirectoryError('Directory check returned no result.');
        return;
      }
      if (row.status === 'found') {
        setDirectoryValidated(true);
        return;
      }
      if (row.status === 'not_found') {
        setDirectoryError(
          row.detail?.includes('Only ')
            ? 'That address is not in an allowed company domain for this check.'
            : 'Not found in the company directory for this address.',
        );
        return;
      }
      setDirectoryError(row.detail || 'Directory lookup was ambiguous; try another address.');
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : e instanceof Error
            ? e.message
            : 'Directory check failed';
      setDirectoryError(typeof msg === 'string' ? msg : 'Directory check failed');
    } finally {
      setDirectoryBusy(false);
    }
  };

  const hasEmail = validationEmail.length > 0;
  if (!canEdit && !canValidate && !hasEmail) {
    return null;
  }

  return (
    <div
      className="mt-2 flex flex-col items-end w-full max-w-md gap-1.5"
      data-testid="profile-header-email-field"
    >
      <div className="flex flex-wrap items-center justify-end gap-2 w-full">
        <span className="text-xs text-dark-text-secondary shrink-0">Email</span>
        {canValidate && (
          <button
            type="button"
            disabled={directoryBusy || directoryValidated}
            onClick={() => void handleValidate()}
            className={
              directoryValidated
                ? 'px-3 py-1.5 rounded-lg bg-dark-border/40 text-dark-text-secondary text-xs font-medium cursor-not-allowed shrink-0'
                : 'px-3 py-1.5 rounded-lg bg-dark-border text-dark-text text-xs font-medium hover:bg-opacity-80 disabled:opacity-50 shrink-0'
            }
            data-testid="profile-header-email-validate"
          >
            {directoryBusy ? 'Validating…' : directoryValidated ? 'validated' : 'Validate'}
          </button>
        )}
      </div>
      {canEdit ? (
        <input
          type="email"
          value={emailDraft}
          onChange={(e) => {
            setEmailDraft(e.target.value);
            setIsSuggested(false);
            setDirectoryValidated(false);
            setDirectoryError(null);
            setContactError(null);
          }}
          onBlur={() => void persistContact()}
          disabled={contactSaving}
          className="w-full px-2.5 py-1.5 rounded-lg bg-dark-bg border border-dark-border text-dark-text text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue disabled:opacity-50 text-right"
          placeholder="name@company.com"
          data-testid="profile-header-email-input"
          aria-label="Work email"
        />
      ) : hasEmail ? (
        <a
          href={`mailto:${validationEmail}`}
          className="w-full text-sm text-brand-blue hover:underline break-all text-right block"
          data-testid="profile-header-email-readonly"
        >
          {validationEmail}
        </a>
      ) : (
        <span className="w-full text-xs text-dark-text-secondary text-right">No email on file</span>
      )}
      {isSuggested && canEdit && (
        <p className="text-[10px] text-dark-text-secondary mt-1 text-right">Suggested from name — edit or save to confirm</p>
      )}
      {contactSaving && <p className="text-[10px] text-dark-text-secondary mt-1 text-right">Saving…</p>}
      {contactError && <p className="text-[11px] text-red-400 mt-1 text-right">{contactError}</p>}
      {directoryError && <p className="text-[11px] text-red-400 mt-1 text-right">{directoryError}</p>}
    </div>
  );
};
